import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ProLayout, PageContainer } from '@ant-design/pro-components';
import { Dropdown, Avatar, Space, Switch, Typography, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/hooks';
import { buildMenuRoutes, resolveActiveLeafPath } from '@/config/navigation';
import { NotificationBell } from './NotificationBell';

const { Text } = Typography;

interface AppLayoutProps {
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
}

/**
 * Main application layout with sidebar navigation
 * Uses Ant Design ProLayout for admin-style layout
 */
export const AppLayout = ({ isDarkMode, onToggleDarkMode }: AppLayoutProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, claims, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();

  // Explicit single-leaf selection. ProLayout's default would also highlight
  // "Overview" (/admin/brokers) on broker sub-pages since it is a strict prefix
  // of its siblings; longest-prefix resolution selects exactly one leaf.
  const activeLeafPath = resolveActiveLeafPath(location.pathname);

  // User dropdown menu items
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: (
        <Space direction="vertical" size={0}>
          <Text strong>{user?.email}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {claims?.adminLevel === 'super' ? 'Super Admin' : 'Admin'}
          </Text>
        </Space>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'theme',
      label: (
        <Space>
          {isDarkMode ? <SunOutlined /> : <MoonOutlined />}
          <span>Dark Mode</span>
          <Switch
            size="small"
            checked={isDarkMode}
            onChange={onToggleDarkMode}
          />
        </Space>
      ),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      danger: true,
      onClick: signOut,
    },
  ];

  return (
    <ProLayout
      title="Trade Gladiator"
      logo={null}
      layout="mix"
      navTheme={isDarkMode ? 'realDark' : 'light'}
      fixSiderbar
      fixedHeader
      collapsed={collapsed}
      onCollapse={setCollapsed}
      location={{ pathname: location.pathname }}
      route={buildMenuRoutes(claims?.adminLevel === 'super')}
      menuItemRender={(item, dom) => (
        <div onClick={() => item.path && navigate(item.path)}>{dom}</div>
      )}
      // The sider is ALWAYS dark navy (see token.sider.colorMenuBackground
      // below), so its menu must render with antd's dark-theme item colors
      // regardless of navTheme. Without this, light navTheme paints near-black
      // item text on the navy background (unreadable) — older pro-components
      // ignored `colorTextMenu` and exposed exactly that bug in production.
      // menuProps.theme is spread last over the antd <Menu theme>, so it wins,
      // and it only affects the sider menu (the top header renders no menu in
      // mix layout), keeping the white header title dark-on-white.
      menuProps={{
        theme: 'dark',
        ...(activeLeafPath ? { selectedKeys: [activeLeafPath] } : {}),
      }}
      token={{
        header: {
          colorBgHeader: isDarkMode ? '#141414' : '#fff',
        },
        sider: {
          // Always dark navy in both light and dark mode (classic admin look).
          colorMenuBackground: '#001529',
          colorTextMenu: 'rgba(255, 255, 255, 0.65)',
          colorTextMenuSelected: '#fff',
          colorBgMenuItemSelected: token.colorPrimary,
        },
      }}
      actionsRender={() => [
        <NotificationBell key="notifications" />,
        <Dropdown
          key="user"
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar
              style={{ backgroundColor: token.colorPrimary }}
              icon={<UserOutlined />}
            />
          </Space>
        </Dropdown>,
      ]}
    >
      <PageContainer>
        <Outlet />
      </PageContainer>
    </ProLayout>
  );
};

export default AppLayout;
